import * as React from 'react'
import {Component, Fragment} from 'react'
import {findDOMNode} from 'react-dom'
import {inject} from 'mobx-react'
import {WidgetApi} from '../../lib/WidgetApi'
import {IFormBase} from '../../lib/forms/IFormBase'
import {isFieldInvalid, isFieldRequired} from '../../lib/helpers'

///////////////////////////////////////////////////////////////////////////////

export type InputWrapperProps = {
  api?: WidgetApi
  children?: any
  form?: IFormBase<any>
  fields?: string | string[]
}

///////////////////////////////////////////////////////////////////////////////

@inject('api')
export class InputWrapper extends Component<InputWrapperProps, any> {
  private input: HTMLInputElement
  private formBlock: HTMLElement
  private fieldBlock: HTMLElement
  private labelBlock: HTMLElement

  constructor(props) {
    super(props)

    this.onInput = this.onInput.bind(this)
    this.onChange = this.onChange.bind(this)
    this.onFocus = this.onFocus.bind(this)
    this.onBlur = this.onBlur.bind(this)
  }

  componentDidMount() {
    this.input = findDOMNode(this) as HTMLInputElement
    this.formBlock = this.input.closest('.form-block') as HTMLElement
    this.fieldBlock = this.input.closest('.field-block') as HTMLElement
    this.labelBlock =
      this.formBlock &&
      (this.formBlock.querySelector('.label-block') as HTMLElement)

    let {input, onInput, onChange, onFocus, onBlur} = this

    if (input) {
      input.addEventListener('input', onInput)
      input.addEventListener('change', onChange)
      input.addEventListener('focus', onFocus)
      input.addEventListener('blur', onBlur)

      this.addIconError()
      this.addPatternAttribute()

      this.toggleFloatingLabelClass()
      this.toggleFilledEmptyClasses()
      this.toggleLabelMarkRequired()
      this.toggleAriaRequired()
      this.toggleAriaInvalid()
      this.toggleHasErrorClass()

      this.addInputWrapperClass()
      this.addMdTransitionClass()
    }
  }

  componentDidUpdate() {
    let {input} = this

    if (input) {
      this.toggleFloatingLabelClass()
      this.toggleFilledEmptyClasses()
      this.toggleLabelMarkRequired()
      this.toggleAriaRequired()
      this.toggleAriaInvalid()
      this.toggleHasErrorClass()

      this.addInputWrapperClass()
      this.addMdTransitionClass()
    }
  }

  componentWillUnmount() {
    let {input, onInput, onChange, onFocus, onBlur} = this

    if (input) {
      input.removeEventListener('input', onInput)
      input.removeEventListener('change', onChange)
      input.removeEventListener('focus', onFocus)
      input.removeEventListener('blur', onBlur)
    }
  }

  onInput(e) {
    this.toggleFilledEmptyClasses()
  }

  onChange(e) {
    this.toggleFilledEmptyClasses()
  }

  onFocus() {
    let {formBlock} = this

    if (formBlock) {
      formBlock.classList.add('input-focused')
    }
  }

  onBlur() {
    let {formBlock} = this

    if (formBlock) {
      formBlock.classList.remove('input-focused')
    }
  }

  addIconError() {
    let {fieldBlock} = this

    if (fieldBlock) {
      let iconsBlock = fieldBlock.querySelector('.icons-block') as HTMLElement

      if (!iconsBlock) {
        iconsBlock = document.createElement('div')
        iconsBlock.classList.add('icons-block')
        fieldBlock.appendChild(iconsBlock)
      }

      let iconError = iconsBlock.querySelector('.icon-error') as HTMLElement

      if (!iconError) {
        iconError = document.createElement('div')
        iconError.classList.add('icon-error')
        iconsBlock.appendChild(iconError)
      }
    }
  }

  addPatternAttribute() {
    // This is needed to force numeric keyboard on iOS.
    // We cannot use "pattern" prop in components because it's already used by IMaskInput,
    // so here we use "data-pattern" instead.

    let {input} = this
    let pattern = input.getAttribute('data-pattern')

    if (!!pattern) {
      input.setAttribute('pattern', pattern)
    }
  }

  toggleFloatingLabelClass() {
    let {formBlock} = this
    let uiFloatingLabels = this.props.api.computedConfig.uiFloatingLabels

    if (formBlock) {
      if (uiFloatingLabels) {
        formBlock.classList.add('floating-label')
        formBlock.classList.remove('static-label')
      } else {
        formBlock.classList.remove('floating-label')
        formBlock.classList.add('static-label')
      }
    }
  }

  toggleFilledEmptyClasses() {
    let {input, formBlock} = this

    if (formBlock) {
      if (input.value && input.value !== '') {
        formBlock.classList.add('input-filled')
        formBlock.classList.remove('input-empty')
      } else {
        formBlock.classList.remove('input-filled')
        formBlock.classList.add('input-empty')
      }
    }
  }

  toggleLabelMarkRequired() {
    let {
      input,
      labelBlock,
      props: {form, fields},
    } = this

    if (input && labelBlock) {
      if (isFieldRequired(form, fields)) {
        let mark = labelBlock.querySelector('.label-mark-required')

        if (!mark) {
          mark = document.createElement('span')
          mark.classList.add('label-mark-required')
          labelBlock.appendChild(mark)
        }
      } else {
        let mark = labelBlock.querySelector('.label-mark-required')

        if (mark) {
          labelBlock.removeChild(mark)
        }
      }
    }
  }

  toggleAriaRequired() {
    let {
      input,
      props: {form, fields},
    } = this

    if (input) {
      if (isFieldRequired(form, fields)) {
        input.setAttribute('aria-required', 'true')
      } else {
        input.removeAttribute('aria-required')
      }
    }
  }

  toggleAriaInvalid() {
    let {
      input,
      props: {form, fields},
    } = this

    if (input) {
      if (isFieldInvalid(form, fields)) {
        input.setAttribute('aria-invalid', 'true')
      } else {
        input.removeAttribute('aria-invalid')
      }
    }
  }

  toggleHasErrorClass() {
    let {
      formBlock,
      props: {form, fields},
    } = this

    if (formBlock) {
      let {debugErrorMessages} = this.props.api.computedConfig

      if (isFieldInvalid(form, fields) || debugErrorMessages) {
        formBlock.classList.add('has-error')
      } else {
        formBlock.classList.remove('has-error')
      }
    }
  }

  addInputWrapperClass() {
    this.formBlock?.classList.add('input-wrapper')
  }

  addMdTransitionClass() {
    setTimeout(() => {
      this.formBlock?.classList.add('md-transition')
    })
  }

  render() {
    return <Fragment>{this.props.children}</Fragment>
  }
}
